import { useEffect, useState } from "react";

import bybTokenLogo from "../../assets/byb.png";
import copyIcon from "../../assets/Copyicon.svg";
import { BYB_CONTRACT_ADDRESS, TOKEN_MARKET_LINKS } from "../../utils/tokenLinks";

async function copyTextToClipboard(text) {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  if (typeof document === "undefined") {
    throw new Error("Clipboard API is not available.");
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();
  textarea.setSelectionRange(0, text.length);

  const didCopy = document.execCommand("copy");
  document.body.removeChild(textarea);

  if (!didCopy) {
    throw new Error("Copy command failed.");
  }
}

/**
 * Bottom-of-page BYB token block. Rendered on both the BiorVault and the
 * BiorSwap landings so the two products share a single terminal CTA. Sized
 * to match the BiorVault download shell (max-w-7xl, same section padding).
 */
export function BybTokenSection() {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const id = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(id);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await copyTextToClipboard(BYB_CONTRACT_ADDRESS);
      setCopied(true);
    } catch (_) {
      setCopied(false);
    }
  };

  return (
    <section className="deferred-section relative px-4 py-10 sm:px-6 sm:py-12 lg:px-8 lg:py-14">
      <div className="mx-auto max-w-7xl">
        <div className="token-spotlight">

          {/* Left: identity */}
          <div className="token-id">
            <div className="token-id__hero">
              <div className="token-id__logo-wrap">
                <div className="token-id__glow" />
                <img src={bybTokenLogo} alt="BYB token" className="token-id__img" />
              </div>

              <div className="token-id__title-wrap">
                <div className="token-id__name">$BYB token</div>
                <div className="token-id__badge">
                  <svg className="token-id__badge-icon" viewBox="0 0 14 22" fill="currentColor" aria-hidden="true">
                    <path fillOpacity=".6" d="M7 0L0 11.2l7 4.12L14 11.2 7 0z" />
                    <path d="M0 12.8L7 22l7-9.2-7 4.12-7-4.12z" />
                    <path fillOpacity=".45" d="M0 11.2L7 15.32 14 11.2 7 7.08 0 11.2z" />
                  </svg>
                  Ethereum Mainnet
                </div>
              </div>
            </div>

            <div className="token-id__contract-block">
              <div className="token-id__contract-card">
                <div className="token-id__contract-text">
                  <div className="token-id__contract-label">Official contract address</div>
                  <code className="token-id__contract-addr" title={BYB_CONTRACT_ADDRESS}>
                    {BYB_CONTRACT_ADDRESS.slice(0, 26)}&hellip;{BYB_CONTRACT_ADDRESS.slice(-8)}
                  </code>
                </div>
                <button
                  type="button"
                  onClick={handleCopy}
                  className={`token-copy-btn ${copied ? "token-copy-btn--copied" : ""}`}
                  aria-label={copied ? "BYB contract copied" : "Copy BYB contract address"}
                  title={copied ? "Copied" : "Copy contract address"}
                >
                  {copied ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true" className="token-copy-btn__icon">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <img src={copyIcon} alt="" aria-hidden="true" className="token-copy-btn__icon" />
                  )}
                </button>
              </div>
              <p className="token-id__hint">
                Always verify this address before interacting with the token.
              </p>
              <span className="sr-only" aria-live="polite">
                {copied ? "BYB contract address copied to clipboard." : ""}
              </span>
            </div>
          </div>

          {/* Separator (visible on mobile only) */}
          <div className="token-spotlight__sep" aria-hidden="true" />

          {/* Right: market links */}
          <div className="token-markets">
            <div className="token-markets__head">
              <span className="token-markets__label">Track $BYB</span>
              <span className="token-markets__sub">Live price &amp; on-chain data</span>
            </div>
            <div className="token-markets__grid">
              {TOKEN_MARKET_LINKS.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                  className="token-market-card"
                >
                  <div className="token-market-card__body">
                    <span className="token-market-card__label">{link.label}</span>
                    <span className="token-market-card__desc">{link.description}</span>
                  </div>
                  <span className="token-market-card__arrow" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 17L17 7" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h8v8" />
                    </svg>
                  </span>
                </a>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

export default BybTokenSection;
